import { Grid } from '@mui/material';
import type { utilizationFleetScore } from 'types/utilization.types';
import { truncateTo2dp, truncateTo3dp } from 'utils/helpers/general';
import { useEffect, useMemo } from 'react';
import {
  type chartDataResponseItem,
  type FuelSummary,
} from 'types/fuelAnalytics';
import useCustomerInfo from 'hooks/useCustomerInfo';
import FuelCardAnalytics from './FuelCardAnalytics';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { isNegative, returnAnalysis } from 'utils/fuelAnalytics';
import { useTranslation } from 'react-i18next';

interface Props {
  enableDarkTheme: boolean;
  isLoading: boolean;
  unitSystem: string;
  summary: FuelSummary;
  excessFuelUsage: number;
  score?: utilizationFleetScore;
  data?: chartDataResponseItem[];
  isSelected: string;
  aggregation?: string;
  customerCode: string;
  shiftNumber: number;
  shiftWiseAnalysis: boolean;
  utilizationType?: string;
  setAnalyticsData: any;
  assetTypeKey: 'fleet' | 'hauler' | 'loader';
}

const FuelUsageGridAnalytics = ({
  enableDarkTheme,
  isLoading,
  summary,
  excessFuelUsage,
  score,
  data,
  aggregation,
  isSelected,
  // customerCode,
  shiftNumber,
  shiftWiseAnalysis,
  utilizationType,
  setAnalyticsData,
  assetTypeKey,
}: Props) => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();

  const entries = Object.values(score ?? {}).filter(
    (item): item is any => typeof item === 'object' && 'asset_type' in item
  );

  // Create key-value pairs of asset_type and util_score
  const keyValueObject = entries?.reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.asset_type] = item.util_score;
      return acc;
    },
    {}
  );
  // Calculate the total average util_score
  const totalSum = entries.reduce(
    (sum, item) => Number(sum) + Number(item.util_score),
    0
  );
  const totalCount = entries.length;
  const totalAverage = totalSum / totalCount;

  // Add the total key to the keyValueObject
  keyValueObject.total = totalAverage;

  const tr = useMemo(
    () => [
      {
        title: t('fuel_usage_total_fuel_consumption'),
        metricKey: 'total_fuel_consumption',
        pointingUp: !isNegative(
          summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`]
        ),
        value: formatNumberToShortScale(
          truncateTo2dp(
            summary?.[`${assetTypeKey}_total_fuel_consumption_l`] || 0
          ),
          2
        ),
        unit: `${unitSystem === 'metric' ? ' L' : ' Gal'}`,
        isGreen: isNegative(
          summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`]
        ),
        analysis: returnAnalysis(
          truncateTo2dp(
            summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`]
          )
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: true,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: item.total_fuel_consumption,
          };
        }),

        isSame:
          summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`] === 0,
        isNotAvailable:
          summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`] ===
            undefined ||
          !summary?.[`${assetTypeKey}_total_fuel_consumption_l_diff`],
      },
      {
        title: t('fuel_rate'),
        metricKey: 'fuel_rate',
        pointingUp: !isNegative(
          summary?.[`${assetTypeKey}_avg_fuel_rate_diff`]
        ),
        value: formatNumberToShortScale(
          truncateTo2dp(summary?.[`${assetTypeKey}_avg_fuel_rate`] || 0),
          2
        ),
        unit: `${unitSystem === 'metric' ? ' L/Hr' : ' Gal/Hr'}`,
        isGreen: isNegative(summary?.[`${assetTypeKey}_avg_fuel_rate_diff`]),
        analysis: returnAnalysis(
          truncateTo2dp(summary?.[`${assetTypeKey}_avg_fuel_rate_diff`])
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: item.avg_fuel_rate,
          };
        }),
        isSame: summary?.[`${assetTypeKey}_avg_fuel_rate_diff`] === 0,
        isNotAvailable:
          !summary?.[`${assetTypeKey}_avg_fuel_rate_diff`] ||
          summary?.[`${assetTypeKey}_avg_fuel_rate_diff`] === undefined,
      },
      {
        title: t('fuel_usage_fuel_consumed_per_ton'),
        metricKey: 'fuel_consumed_per_ton',
        pointingUp: !isNegative(
          summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]
        ),
        value: formatNumberToShortScale(
          truncateTo3dp(
            summary?.[`${assetTypeKey}_fuel_consumed_per_ton`] || 0
          ),
          4
        ),
        unit: `${unitSystem === 'metric' ? ' L/Ton' : ' Gal/Ton'}`,
        isGreen: isNegative(
          summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]
        ),
        analysis:
          (Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]) ===
            -100 ||
            Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]) ===
              0) &&
          Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton`]) === 0
            ? returnAnalysis(0)
            : returnAnalysis(
                truncateTo3dp(
                  summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]
                )
              ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: truncateTo3dp(item.fuel_consumed_per_ton),
          };
        }),
        isSame: summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`] === 0,
        isNotAvailable:
          ((Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]) ===
            -100 ||
            Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`]) ===
              0) &&
            Number(summary?.[`${assetTypeKey}_fuel_consumed_per_ton`]) === 0) ||
          !summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`] ||
          summary?.[`${assetTypeKey}_fuel_consumed_per_ton_diff`] === undefined,
      },
      {
        title: t('fuel_usage_co2_emitted'),
        metricKey: 'co2_emitted',
        pointingUp: !isNegative(summary?.[`${assetTypeKey}_CO2_used_diff`]),
        value: formatNumberToShortScale(
          truncateTo2dp(summary?.[`${assetTypeKey}_CO2_used`] || 0),
          2
        ),
        unit: `Kg`,
        isGreen: isNegative(summary?.[`${assetTypeKey}_CO2_used_diff`]),
        analysis: returnAnalysis(
          truncateTo2dp(summary?.[`${assetTypeKey}_CO2_used_diff`])
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: item.CO2_used,
          };
        }),
        isSame: summary?.[`${assetTypeKey}_CO2_used_diff`] === 0,
        isNotAvailable:
          !summary?.[`${assetTypeKey}_CO2_used_diff`] ||
          summary?.[`${assetTypeKey}_CO2_used_diff`] === undefined,
      },
      {
        title: t('fuel_usage_cost_incurred'),
        metricKey: 'cost_incurred',
        pointingUp: !isNegative(
          summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`]
        ),
        value: formatNumberToShortScale(
          truncateTo2dp(summary?.[`${assetTypeKey}_fuel_cost_incurred`] || 0),
          2
        ),
        unit: '$',
        isGreen: isNegative(
          summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`]
        ),
        analysis: returnAnalysis(
          truncateTo2dp(summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`])
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: item.fuel_cost_incurred,
          };
        }),
        isSame: summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`] === 0,
        isNotAvailable:
          !summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`] ||
          summary?.[`${assetTypeKey}_fuel_cost_incurred_diff`] === undefined,
      },
      {
        title: t('fuel_usage_potential_fuel_savings'),
        metricKey: 'potential_fuel_savings',
        pointingUp: !isNegative(summary?.[`${assetTypeKey}_fuel_savings_diff`]),
        value: truncateTo2dp(summary?.[`${assetTypeKey}_fuel_savings`] || 0),
        unit: '%',
        isGreen: isNegative(summary?.[`${assetTypeKey}_fuel_savings_diff`]),
        analysis:
          (Number(summary?.[`${assetTypeKey}_fuel_savings_diff`]) === -100 ||
            Number(summary?.[`${assetTypeKey}_fuel_savings_diff`]) === 0) &&
          Number(summary?.[`${assetTypeKey}_fuel_savings`]) === 0
            ? returnAnalysis(0)
            : returnAnalysis(
                truncateTo2dp(summary?.[`${assetTypeKey}_fuel_savings_diff`])
              ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: data?.map((item) => {
          return {
            name: item.date,
            value: item.fuel_savings,
          };
        }),
        isSame: Number(summary?.[`${assetTypeKey}_fuel_savings_diff`]) === 0,
        isNotAvailable:
          ((Number(summary?.[`${assetTypeKey}_fuel_savings_diff`]) === -100 ||
            Number(summary?.[`${assetTypeKey}_fuel_savings_diff`]) === 0) &&
            Number(summary?.[`${assetTypeKey}_fuel_savings`]) === 0) ||
          !summary?.[`${assetTypeKey}_fuel_savings_diff`] ||
          summary?.[`${assetTypeKey}_fuel_savings_diff`] === undefined,
      },
    ],
    [
      t,
      assetTypeKey,
      summary,
      aggregation,
      isSelected,
      shiftWiseAnalysis,
      shiftNumber,
      unitSystem,
      data,
      // shift.startHour,
      // shift.endHour,
      // utilizationScore,
      // utilizationChart,
      // engineHoursChart,
      // idlingHoursChart,
      // productiveHoursChart,
    ]
  );

  useEffect(() => {
    setAnalyticsData((prev: any) =>
      JSON.stringify(prev) !== JSON.stringify(tr) ? tr : prev
    );
  }, [tr, setAnalyticsData]);

  return (
    <Grid
      container
      spacing={1}
      justifyContent="space-between"
      sx={{
        paddingTop: '15px',
      }}
    >
      {tr.map((item: any, index: any) => {
        return (
          <Grid item key={index} lg={2} md={6} sm={12} xs={12}>
            <FuelCardAnalytics
              enableDarkTheme={enableDarkTheme}
              analytics={item}
              isLoading={isLoading}
              isSelected={isSelected}
              aggregation={aggregation}
              utilizationType={utilizationType}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default FuelUsageGridAnalytics;
